const PFA = {
    init: async function () {
        const customizerRoot = document.querySelector('#popup-chat-customizer');
        if (!customizerRoot) {
            return;
        }

        const productHandle = customizerRoot.dataset.productHandle;

        let productData;
        try {
            productData = await Utility.fetchProductDataAjax(productHandle);
        } catch (error) {
            console.error('PFA, failed to fetch product data:', error);
            return;
        }

        // Initial Global Stock Check: Disable button if the entire product is sold out
        const anyAvailable = productData.variants.some(v => v.available);
        const addButton = customizerRoot.querySelector('[data-customizer-add]');
        const isRestrictedEA = customizerRoot.dataset.restrictedEa === 'true';

        if (addButton) {
            if (isRestrictedEA) {
                addButton.classList.add('disabled-btn', 'early-access-restricted');
                addButton.disabled = true;
                addButton.textContent = 'Early Access Only';
            } else if (!anyAvailable) {
                addButton.classList.add('out-of-stock-main', 'disabled-btn');
                addButton.classList.remove('early-access-restricted');
                addButton.disabled = true;
                addButton.textContent = 'Out of Stock';
            }

            // The AJAX cart script likely stops propagation, hiding the click from the global listener.
            // We manually fire the ATC event directly at the source. This ensures it fires even
            // if the user clicks Add To Cart on the default variant without changing any swatches.
            addButton.addEventListener('click', () => {
                if (typeof fireEvent === 'function') {
                    fireEvent(addButton);
                }
            });
        }

        const injectedWrapper = customizerRoot.parentElement || document;

        const context = {
            parent: injectedWrapper,
            productData: productData,
            onVariantChange: (matchingVariant) => {
                this._handleVariantChange(matchingVariant, customizerRoot, productData, injectedWrapper);
            }
        };

        const swatchButtons = Array.from(injectedWrapper.querySelectorAll('.swatch-btn'));
        swatchButtons.forEach((swatchButton) => {
            swatchButton.addEventListener('click', async (event) => {
                await Shared.handleSwatchChange(event.currentTarget, false, false, context);
            });
        });

        const optionSelects = Array.from(injectedWrapper.querySelectorAll('select'));
        optionSelects.forEach((selectElement) => {
            const hasSwatchOptions = selectElement.querySelector('option.swatch-btn,[data-option-name],[data-swatch-value]');
            if (!hasSwatchOptions) {
                return;
            }

            selectElement.addEventListener('change', async (event) => {
                // Find the currently selected <option> and manually fire its GA4 event
                const selectedOption = event.currentTarget.options[event.currentTarget.selectedIndex];
                if (selectedOption && selectedOption.hasAttribute('data-ga-event') && typeof fireEvent === 'function') {
                    fireEvent(selectedOption);
                }

                await Shared.handleSwatchChange(event.currentTarget, false, false, context);

                // Force the color swatches to re-evaluate based on the new Shape/Weight selection
                this._updateSwatchVisibility(productData, injectedWrapper);
            });
        });

        const decreaseButton = injectedWrapper.querySelector('[data-customizer-qty-decrease]');
        const increaseButton = injectedWrapper.querySelector('[data-customizer-qty-increase]');

        if (decreaseButton) {
            decreaseButton.addEventListener('click', () => {
                Shared.handleQuantityChange(decreaseButton, 'decrease', false, context);
            });
        }

        if (increaseButton) {
            increaseButton.addEventListener('click', () => {
                Shared.handleQuantityChange(increaseButton, 'increase', false, context);
            });
        }

        await new Promise((resolve) => requestAnimationFrame(resolve));

        const firstSelect = injectedWrapper.querySelector('select.swatch-btn-select') || injectedWrapper.querySelector('select');
        if (firstSelect) {
            await Shared.handleSwatchChange(firstSelect, false, false, context);
        } else {
            const initialSelectedSwatch = injectedWrapper.querySelector('.swatch-btn.selected');
            if (initialSelectedSwatch) {
                await Shared.handleSwatchChange(initialSelectedSwatch, false, false, context);
            }
        }
    },

    /**
     * Handles DOM updates when a new variant is selected.
     */
    _handleVariantChange: function (matchingVariant, customizerRoot, productData, injectedWrapper) {
        const variantInput = customizerRoot.querySelector('[data-customizer-variant-input]');
        if (variantInput) {
            variantInput.value = String(matchingVariant.id);
        }

        const addButton = customizerRoot.querySelector('[data-customizer-add]');
        const isRestrictedEA = customizerRoot.dataset.restrictedEa === 'true';

        if (addButton) {
            // Per-Variant Stock Check: Toggle classes based on current variant availability and restrictions
            if (isRestrictedEA) {
                addButton.classList.add('disabled-btn', 'early-access-restricted');
                addButton.disabled = true;
                addButton.textContent = 'Early Access Only';
            } else if (matchingVariant && matchingVariant.available) {
                addButton.classList.remove('out-of-stock-main', 'disabled-btn', 'early-access-restricted');
                addButton.disabled = false;
                addButton.textContent = 'Add to cart';
            } else {
                addButton.classList.add('out-of-stock-main', 'disabled-btn');
                addButton.classList.remove('early-access-restricted');
                addButton.disabled = true;
                addButton.textContent = 'Out of Stock';
            }

            // GA4 Event tracking dynamically based on current variant selection
            // Logic: pop_prod_[Variant ID]_atc
            const variantId = String(matchingVariant.id);
            addButton.dataset.gaEvent = `pop_prod_${variantId}_atc`;

            // Update Title for GA4 reporting to include specific variant name
            const vTitle = matchingVariant.title && matchingVariant.title !== 'Default Title'
                ? ` - ${matchingVariant.title}`
                : '';
            addButton.dataset.title = `ATC: ${productData.title}${vTitle}`;

            addButton.dataset.variantId = variantId;
            addButton.dataset.variantTitle = matchingVariant.title && matchingVariant.title !== 'Default Title'
                ? matchingVariant.title
                : '';

            const nextImageUrl = matchingVariant.featured_image && matchingVariant.featured_image.src
                ? matchingVariant.featured_image.src
                : '';
            if (nextImageUrl) {
                addButton.dataset.productImageUrl = nextImageUrl;
            }
        }

        const subtotalElement = customizerRoot.querySelector('[data-customizer-subtotal]');
        if (subtotalElement) {
            const qtyInput = customizerRoot.querySelector('[data-customizer-qty-input]');
            const currentQty = qtyInput ? Number(qtyInput.value) : 1;
            const priceCents = Number(matchingVariant.price) || 0;
            const subtotalCents = priceCents * currentQty;

            subtotalElement.textContent = `$${(subtotalCents / 100).toFixed(2)}`;
        }

        const selectedColorValueElement = customizerRoot.querySelector(
            '[data-customizer-color-label] .popup-chat-customizer__swatch-value'
        );

        if (selectedColorValueElement) {
            const selectedColorSwatch = customizerRoot.querySelector(
                '.swatch-btn.selected[data-option-name="color"]'
            );

            if (selectedColorSwatch) {
                selectedColorValueElement.textContent = selectedColorSwatch.dataset.swatchValue;
            }
        }

        this._updateSwatchVisibility(productData, injectedWrapper);
    },

    /**
     * Evaluates valid option combinations and hides unavailable color swatches dynamically.
     * Automatically accounts for Shape, Weight, or any other non-color option in the dropdowns.
     */
    _updateSwatchVisibility: function (productData, injectedWrapper) {
        const normalize = (val) => String(val || "").toLowerCase().trim();

        // Find all visual Color swatches
        const colorSwatches = Array.from(injectedWrapper.querySelectorAll('.swatch-btn'))
            .filter(s => normalize(s.dataset.optionName) === 'color' && s.tagName.toLowerCase() !== 'option');

        if (colorSwatches.length === 0) return;

        // Gather all currently selected non-color values (Shape, Weight, etc.)
        const selectedNonColorValues = [];

        // Grab selections from dropdown selectors
        const activeSelects = injectedWrapper.querySelectorAll('select.swatch-btn-select');
        activeSelects.forEach(select => {
            const selectedOption = select.options[select.selectedIndex];
            if (selectedOption && normalize(selectedOption.dataset.optionName) !== 'color') {
                selectedNonColorValues.push(normalize(selectedOption.value));
            }
        });

        // (Fallback) Just in case we ever add non-color buttons in the future
        const activeButtons = injectedWrapper.querySelectorAll('.swatch-btn.selected');
        activeButtons.forEach(btn => {
            if (btn.tagName.toLowerCase() !== 'option' && normalize(btn.dataset.optionName) !== 'color') {
                selectedNonColorValues.push(normalize(btn.dataset.swatchValue));
            }
        });

        // Evaluate color visibility against all selected dropdown options
        let firstVisibleColor = null;
        let currentlySelectedIsHidden = false;

        colorSwatches.forEach(swatch => {
            const colorValue = normalize(swatch.dataset.swatchValue);

            const isValidCombination = productData.variants.some(variant => {
                const variantOptions = variant.options.map(opt => normalize(opt));

                if (!variantOptions.includes(colorValue)) return false;

                return selectedNonColorValues.every(val => variantOptions.includes(val));
            });

            if (isValidCombination) {
                swatch.style.setProperty('display', 'flex', 'important');
                swatch.classList.remove('hide-swatch');
                if (!firstVisibleColor) firstVisibleColor = swatch;
            } else {
                swatch.style.setProperty('display', 'none', 'important');
                swatch.classList.add('hide-swatch');
                if (swatch.classList.contains('selected')) {
                    currentlySelectedIsHidden = true;
                }
            }
        });

        // Auto-select the first valid color if the current selection becomes invalid
        if (currentlySelectedIsHidden && firstVisibleColor) {
            firstVisibleColor.click();
        }
    }
};

window.Jarvis = window.Jarvis || {};
window.Jarvis.PFA = PFA;